import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';

import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { 
  MessageSquare, 
  Bug, 
  Lightbulb, 
  MoreHorizontal,
  CheckCircle,
  Clock,
  Archive,
  Eye,
  Smartphone,
  Globe,
  Apple
} from 'lucide-react';

import CustomPagination from '../../CustomPagination';
import CommonTable from '../../CommonTable/CommonTable';
import Loader from '../../Loader';

import s from './FeedbackManagement.css';
import cs from '../../../components/commonStyle.css';

const GET_ALL_FEEDBACK = gql`
  mutation getAllUserFeedback($currentPage: Int, $searchList: String, $filterType: String, $filterStatus: String) {
    getAllUserFeedback(currentPage: $currentPage, searchList: $searchList, filterType: $filterType, filterStatus: $filterStatus) {
      status
      errorMessage
      count
      results {
        id
        userId
        type
        message
        status
        adminNotes
        platform
        createdAt
        userName
        userEmail
      }
    }
  }
`;

const UPDATE_FEEDBACK_STATUS = gql`
  mutation updateUserFeedbackStatus($id: Int!, $status: String, $adminNotes: String) {
    updateUserFeedbackStatus(id: $id, status: $status, adminNotes: $adminNotes) {
      status
      errorMessage
    }
  }
`;

class FeedbackManagement extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      currentPage: 1,
      searchList: '',
      filterType: 'all',
      filterStatus: 'all',
      feedbacks: [],
      count: 0,
      loading: true,
      selectedFeedback: null,
      showModal: false,
      typing: false,
      typingTimeout: 0
    };
  }

  componentDidMount() {
    this.loadFeedbacks();
  }

  loadFeedbacks = async () => {
    const { currentPage, searchList, filterType, filterStatus } = this.state;
    this.setState({ loading: true });

    try {
      const { data } = await this.props.client.mutate({
        mutation: GET_ALL_FEEDBACK,
        variables: { currentPage, searchList, filterType, filterStatus }
      });

      if (data.getAllUserFeedback.status === 200) {
        this.setState({
          feedbacks: data.getAllUserFeedback.results || [],
          count: data.getAllUserFeedback.count || 0,
          loading: false
        });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error('Error loading feedbacks:', error);
      this.setState({ loading: false });
    }
  };

  handleSearchChange = (e) => {
    const self = this;
    if (self.state.typingTimeout) {
      clearTimeout(self.state.typingTimeout);
    }
    self.setState({
      searchList: e.target.value,
      typing: false,
      typingTimeout: setTimeout(() => {
        self.setState({ currentPage: 1 }, () => self.loadFeedbacks());
      }, 450)
    });
  };

  handleFilterType = (e) => {
    this.setState({ filterType: e.target.value, currentPage: 1 }, () => this.loadFeedbacks());
  };

  handleFilterStatus = (e) => {
    this.setState({ filterStatus: e.target.value, currentPage: 1 }, () => this.loadFeedbacks());
  };

  paginationData = (currentPage) => {
    this.setState({ currentPage }, () => this.loadFeedbacks());
  };

  updateStatus = async (id, status) => {
    try {
      const { data } = await this.props.client.mutate({
        mutation: UPDATE_FEEDBACK_STATUS,
        variables: { id, status }
      });

      if (data.updateUserFeedbackStatus.status === 200) {
        this.loadFeedbacks();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  getTypeIcon = (type) => {
    switch (type) {
      case 'Bug': return <Bug size={16} className={s.iconBug} />;
      case 'Suggestion': return <Lightbulb size={16} className={s.iconSuggestion} />;
      default: return <MessageSquare size={16} className={s.iconFeedback} />;
    }
  };

  getPlatformIcon = (platform) => {
    switch (platform) {
      case 'ios': return <Apple size={14} />;
      case 'android': return <Smartphone size={14} />;
      default: return <Globe size={14} />;
    }
  };

  getStatusBadge = (status) => {
    const badges = {
      pending: { icon: <Clock size={12} />, class: s.badgePending, label: 'Pending' },
      reviewed: { icon: <Eye size={12} />, class: s.badgeReviewed, label: 'Reviewed' },
      resolved: { icon: <CheckCircle size={12} />, class: s.badgeResolved, label: 'Resolved' },
      archived: { icon: <Archive size={12} />, class: s.badgeArchived, label: 'Archived' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={cx(s.statusBadge, badge.class)}>
        {badge.icon} {badge.label}
      </span>
    );
  };

  formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  thead = () => {
    return [
      { data: 'Type' },
      { data: 'User' },
      { data: 'Message' },
      { data: 'Platform' },
      { data: 'Status' },
      { data: 'Date' },
      { data: 'Actions' }
    ];
  };

  tbody = () => {
    const { feedbacks } = this.state;

    return feedbacks.map((feedback, key) => ({
      id: key,
      data: [
        { 
          data: (
            <div className={s.typeCell}>
              {this.getTypeIcon(feedback.type)}
              <span>{feedback.type}</span>
            </div>
          )
        },
        { 
          data: (
            <div className={s.userCell}>
              <div className={s.userName}>{feedback.userName}</div>
              <div className={s.userEmail}>{feedback.userEmail}</div>
            </div>
          )
        },
        { 
          data: (
            <div className={s.messageCell} title={feedback.message}>
              {feedback.message.length > 80 
                ? feedback.message.substring(0, 80) + '...' 
                : feedback.message}
            </div>
          )
        },
        { 
          data: (
            <div className={s.platformCell}>
              {this.getPlatformIcon(feedback.platform)}
              <span>{feedback.platform || 'web'}</span>
            </div>
          )
        },
        { data: this.getStatusBadge(feedback.status) },
        { data: <span className={s.dateCell}>{this.formatDate(feedback.createdAt)}</span> },
        { 
          data: (
            <div className={s.actionsCell}>
              <select 
                className={s.statusSelect}
                value={feedback.status}
                onChange={(e) => this.updateStatus(feedback.id, e.target.value)}
              >
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="resolved">Resolved</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          )
        }
      ]
    }));
  };

  render() {
    const { currentPage, count, loading, filterType, filterStatus } = this.state;

    return (
      <div className={cx(s.pagecontentWrapper)}>
        <div className={s.contentBox}>
          <div className={s.headerSection}>
            <h1 className={s.headerTitle}>
              <MessageSquare size={24} className={s.headerIcon} />
              User Feedback Management
            </h1>
            <div className={s.statsRow}>
              <div className={s.statCard}>
                <span className={s.statNumber}>{count}</span>
                <span className={s.statLabel}>Total Feedbacks</span>
              </div>
            </div>
          </div>

          <div className={s.filtersRow}>
            <select 
              className={s.filterSelect} 
              value={filterType}
              onChange={this.handleFilterType}
            >
              <option value="all">All Types</option>
              <option value="Bug">Bug Reports</option>
              <option value="Feedback">Feedback</option>
              <option value="Suggestion">Suggestions</option>
              <option value="Other">Other</option>
            </select>

            <select 
              className={s.filterSelect}
              value={filterStatus}
              onChange={this.handleFilterStatus}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
              <option value="resolved">Resolved</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {loading ? (
            <Loader type="text" />
          ) : (
            <>
              <CommonTable
                thead={this.thead}
                tbody={this.tbody}
                title=""
                isSearch
                onSearch={this.handleSearchChange}
              />
              {count > 0 && (
                <div className={s.paginationWrapper}>
                  <CustomPagination
                    total={count}
                    currentPage={currentPage}
                    defaultCurrent={1}
                    defaultPageSize={10}
                    change={this.paginationData}
                    paginationLabel="feedbacks"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
}

const mapState = (state) => ({});
const mapDispatch = {};

export default compose(
  injectIntl,
  withStyles(s, cs),
  connect(mapState, mapDispatch)
)(FeedbackManagement);
